/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, AlertDescription, AlertIcon, Box, HStack, Input as ChakraInput, SimpleGrid, Spinner, useToast, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, isBefore, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Text } from '../../../../components/atoms/Text';
import { Button } from '../../../../components/atoms/Button';
import { useTheme } from '../../../../contexts/ThemeProvider';
import { masks } from '../../../../utils/masks';
import {
  BookingDraft,
  getAvailableSlots,
  SchedulingDay,
  SchedulingSlot,
} from '../../../../service/telemedicine/api';
import { parseDateKey, toDateKey } from '../../utils/date';

interface SchedulingStepProps {
  patientName?: string;
  patientEmail?: string;
  patientCpf?: string;
  patientPhone?: string;
  patientBirthDate?: string;
  planName: string;
  mappedPlanName: string;
  subscriptionId?: string;
  /** Não reserva nada ainda — só empacota a escolha pro passo seguinte confirmar. */
  onContinue: (draft: BookingDraft) => void;
}

type LoadPhase = 'loading' | 'error' | 'ready';

const WEEKDAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const TOAST_ERROR = (message: string) => ({
  title: message,
  status: 'error' as const,
  duration: 9000,
  isClosable: true,
});

// Semanas (dom-sáb) cobrindo o mês inteiro de `monthDate`, incluindo dias de
// preenchimento do mês anterior/seguinte pra fechar as semanas — 6 linhas fixas,
// pra não "pular" de layout entre meses com 4, 5 ou 6 semanas.
function buildMonthGrid(monthDate: Date): Date[][] {
  const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

export function SchedulingStep({
  patientName,
  patientEmail,
  patientCpf,
  patientPhone,
  patientBirthDate,
  onContinue,
}: SchedulingStepProps) {
  const { colors } = useTheme();
  const toast = useToast();

  const [phase, setPhase] = useState<LoadPhase>('loading');
  const [days, setDays] = useState<SchedulingDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SchedulingSlot | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(new Date()));

  // O perfil do paciente pode não ter nome/e-mail/telefone completos (esses campos são
  // opcionais no cadastro) — em vez de travar o agendamento com um erro sem saída, deixamos
  // editáveis aqui, pré-preenchidos com o que já existir no perfil. Mesma lógica já usada
  // pra data de nascimento, que nunca existe no perfil hoje.
  const [name, setName] = useState(patientName ?? '');
  const [email, setEmail] = useState(patientEmail ?? '');
  const [phone, setPhone] = useState(patientPhone ?? '');
  const [birthDate, setBirthDate] = useState(patientBirthDate ?? '');

  // Layout empilhado (form -> calendário -> horário -> resumo): rola pra seção seguinte a
  // cada escolha, já que ela só aparece mais abaixo na tela depois da anterior.
  const timeSectionRef = useRef<HTMLDivElement | null>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => setName((current) => current || patientName || ''), [patientName]);
  useEffect(() => setEmail((current) => current || patientEmail || ''), [patientEmail]);
  useEffect(() => setPhone((current) => current || patientPhone || ''), [patientPhone]);

  const fetchDays = useCallback(async () => {
    setPhase('loading');

    try {
      const response = await getAvailableSlots();
      const availableDays = response.data ?? [];
      setDays(availableDays);

      const firstDayWithSlots = availableDays.find((day) => day.slots?.length) ?? availableDays[0];
      setSelectedDate(firstDayWithSlots?.date ?? null);
      if (firstDayWithSlots?.date) setVisibleMonth(startOfMonth(parseDateKey(firstDayWithSlots.date)));
      setPhase('ready');
    } catch {
      setPhase('error');
      toast(TOAST_ERROR('Não foi possível carregar os horários disponíveis.'));
    }
  }, [toast]);

  useEffect(() => {
    fetchDays();
  }, [fetchDays]);

  function handleSelectDate(dateKey: string) {
    setSelectedDate(dateKey);
    setSelectedSlot(null);
    timeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const daysByDate = useMemo(() => {
    const map = new Map<string, SchedulingDay>();
    days.forEach((day) => map.set(day.date, day));
    return map;
  }, [days]);

  const selectedDay = selectedDate ? daysByDate.get(selectedDate) ?? null : null;
  const monthWeeks = useMemo(() => buildMonthGrid(visibleMonth), [visibleMonth]);
  const today = startOfDay(new Date());
  const monthLabel = format(visibleMonth, "MMMM 'de' yyyy", { locale: ptBR });

  function handleSelectSlot(slot: SchedulingSlot) {
    setSelectedSlot((current) => (current?.slot_id === slot.slot_id ? null : slot));
  }

  useEffect(() => {
    if (!selectedSlot) return;
    summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedSlot]);

  const handleContinue = useCallback(() => {
    if (!selectedDate || !selectedSlot) return;

    if (!name.trim() || !email.trim() || !phone.trim() || !patientCpf) {
      toast(TOAST_ERROR('Preencha nome, e-mail e telefone para confirmar o agendamento.'));
      return;
    }

    if (!birthDate) {
      toast(TOAST_ERROR('Informe a data de nascimento do paciente para confirmar o agendamento.'));
      return;
    }

    onContinue({
      date: selectedDate,
      slot: selectedSlot,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      birthDate,
      cpf: patientCpf,
    });
  }, [birthDate, email, name, onContinue, patientCpf, phone, selectedDate, selectedSlot, toast]);

  return (
    <VStack align={'stretch'} spacing={4} width={'100%'}>
      <Text fontSize={'14px'} color={'#4A5568'}>
        Atendimento com <strong>Clínico Geral</strong>. Escolha o horário disponível — assim que o atendimento for
        confirmado, você segue automaticamente para o próximo passo.
      </Text>

      <VStack align={'stretch'} spacing={3} width={'100%'}>
        {(!patientName || !name) && (
          <Box width={'100%'}>
            <Text fontSize={'14px'} fontWeight={600} marginBottom={2}>
              Nome completo do paciente
            </Text>
            <Box backgroundColor={'#FAFAF9'} borderRadius={'16px'} border={'2px solid #EBEBE6'} padding={'4px 12px'}>
              <ChakraInput
                value={name}
                onChange={(event) => setName(event.target.value)}
                border={'0px'}
                variant={'unstyled'}
                color={'#292927'}
                fontSize={'16px'}
                minHeight={'40px'}
                placeholder={'Nome completo'}
              />
            </Box>
          </Box>
        )}

        {(!patientEmail || !email) && (
          <Box width={'100%'}>
            <Text fontSize={'14px'} fontWeight={600} marginBottom={2}>
              E-mail do paciente
            </Text>
            <Box backgroundColor={'#FAFAF9'} borderRadius={'16px'} border={'2px solid #EBEBE6'} padding={'4px 12px'}>
              <ChakraInput
                type={'email'}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                border={'0px'}
                variant={'unstyled'}
                color={'#292927'}
                fontSize={'16px'}
                minHeight={'40px'}
                placeholder={'email@exemplo.com'}
              />
            </Box>
          </Box>
        )}

        {(!patientPhone || !phone) && (
          <Box width={'100%'}>
            <Text fontSize={'14px'} fontWeight={600} marginBottom={2}>
              Telefone do paciente
            </Text>
            <Box backgroundColor={'#FAFAF9'} borderRadius={'16px'} border={'2px solid #EBEBE6'} padding={'4px 12px'}>
              <ChakraInput
                value={phone}
                onChange={(event) => setPhone(masks.cell(event.target.value))}
                border={'0px'}
                variant={'unstyled'}
                color={'#292927'}
                fontSize={'16px'}
                minHeight={'40px'}
                placeholder={'(00) 00000-0000'}
              />
            </Box>
          </Box>
        )}

        <Box width={'100%'}>
          <Text fontSize={'14px'} fontWeight={600} marginBottom={2}>
            Data de nascimento do paciente
          </Text>
          <Box backgroundColor={'#FAFAF9'} borderRadius={'16px'} border={'2px solid #EBEBE6'} padding={'4px 12px'}>
            <ChakraInput
              type="date"
              value={birthDate}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(event) => setBirthDate(event.target.value)}
              border={'0px'}
              variant={'unstyled'}
              color={'#292927'}
              fontSize={'16px'}
              minHeight={'40px'}
            />
          </Box>
        </Box>
      </VStack>

      {phase === 'loading' && (
        <VStack spacing={3} paddingY={6}>
          <Spinner color={colors.primaryColor} size={'lg'} />
          <Text fontSize={'14px'} color={'#4A5568'}>
            Buscando horários disponíveis...
          </Text>
        </VStack>
      )}

      {phase === 'error' && (
        <Alert status={'error'} borderRadius={'md'} alignItems={'flex-start'}>
          <AlertIcon />
          <VStack align={'stretch'} spacing={2} flex={1}>
            <AlertDescription fontSize={'13px'}>
              Não foi possível carregar os horários disponíveis. Tente novamente.
            </AlertDescription>
            <Button maxWidth={'160px'} minHeight={'40px'} fontSize={'14px'} onClick={fetchDays}>
              Tentar novamente
            </Button>
          </VStack>
        </Alert>
      )}

      {phase === 'ready' && days.length === 0 && (
        <Alert status={'info'} borderRadius={'md'}>
          <AlertIcon />
          <AlertDescription fontSize={'13px'}>
            Não há horários disponíveis no momento. Tente novamente mais tarde.
          </AlertDescription>
        </Alert>
      )}

      {phase === 'ready' && days.length > 0 && (
        <VStack align={'stretch'} spacing={3} width={'100%'}>
          <Box border={'1px solid #E2E8F0'} borderRadius={'16px'} padding={'12px'}>
            <HStack width={'100%'} justify={'space-between'} marginBottom={3}>
              <Button
                width={'32px'}
                minHeight={'32px'}
                padding={0}
                fontSize={'16px'}
                variant={'outline'}
                onClick={() => setVisibleMonth((current) => addDays(startOfMonth(current), -1))}
                aria-label={'Mês anterior'}
              >
                ‹
              </Button>
              <Text fontSize={'14px'} fontWeight={700} textTransform={'capitalize'}>
                {monthLabel}
              </Text>
              <Button
                width={'32px'}
                minHeight={'32px'}
                padding={0}
                fontSize={'16px'}
                variant={'outline'}
                onClick={() => setVisibleMonth((current) => addDays(startOfMonth(addDays(current, 32)), 0))}
                aria-label={'Próximo mês'}
              >
                ›
              </Button>
            </HStack>

            <SimpleGrid columns={7} spacing={1} marginBottom={1}>
              {WEEKDAY_LABELS.map((label, index) => (
                <Text key={`${label}-${index}`} textAlign={'center'} fontSize={'11px'} color={'#4A5568'} fontWeight={600}>
                  {label}
                </Text>
              ))}
            </SimpleGrid>

            {monthWeeks.map((week, weekIndex) => (
              <SimpleGrid columns={7} spacing={1} key={weekIndex} marginBottom={1}>
                {week.map((day) => {
                  const dateKey = toDateKey(day);
                  const dayData = daysByDate.get(dateKey);
                  const hasSlots = (dayData?.slots?.length ?? 0) > 0;
                  const isCurrentMonth = isSameMonth(day, visibleMonth);
                  const isPast = isBefore(day, today) && !isSameDay(day, today);
                  const isSelected = dateKey === selectedDate;
                  const isSelectable = hasSlots && isCurrentMonth && !isPast;

                  return (
                    <Box
                      key={dateKey}
                      role={isSelectable ? 'button' : undefined}
                      tabIndex={isSelectable ? 0 : -1}
                      textAlign={'center'}
                      paddingY={'8px'}
                      borderRadius={'10px'}
                      cursor={isSelectable ? 'pointer' : 'default'}
                      opacity={!isCurrentMonth ? 0.25 : isSelectable ? 1 : 0.35}
                      border={'1px solid'}
                      borderColor={isSelected ? colors.primaryColor : 'transparent'}
                      backgroundColor={isSelected ? '#F0FFF4' : hasSlots && isCurrentMonth && !isPast ? '#FAFAF9' : 'transparent'}
                      onClick={() => isSelectable && handleSelectDate(dateKey)}
                      onKeyDown={(event) => {
                        if (isSelectable && (event.key === 'Enter' || event.key === ' ')) handleSelectDate(dateKey);
                      }}
                    >
                      <Text fontSize={'13px'} fontWeight={isSelected ? 700 : 400}>
                        {format(day, 'd')}
                      </Text>
                    </Box>
                  );
                })}
              </SimpleGrid>
            ))}
          </Box>

          <Box ref={timeSectionRef}>
            <Text fontSize={'14px'} fontWeight={600} marginBottom={2}>
              Horário {selectedDate && `— ${format(parseDateKey(selectedDate), "dd 'de' MMMM", { locale: ptBR })}`}
            </Text>

            {(!selectedDay || (selectedDay.slots?.length ?? 0) === 0) && (
              <Text fontSize={'13px'} color={'#4A5568'}>
                Selecione, no calendário acima, um dia com horários disponíveis.
              </Text>
            )}

            {selectedDay && (selectedDay.slots?.length ?? 0) > 0 && (
              <HStack spacing={2} flexWrap={'wrap'}>
                {selectedDay.slots.map((slot) => {
                  const isSlotSelected = selectedSlot?.slot_id === slot.slot_id;

                  return (
                    <Button
                      key={slot.slot_id}
                      width={'auto'}
                      minWidth={'88px'}
                      minHeight={'44px'}
                      fontSize={'14px'}
                      variant={isSlotSelected ? 'solid' : 'outline'}
                      backgroundColor={isSlotSelected ? colors.primaryColor : '#FFFFFF'}
                      color={isSlotSelected ? 'white' : colors.primaryColor}
                      border={'1px solid'}
                      borderColor={colors.primaryColor}
                      onClick={() => handleSelectSlot(slot)}
                    >
                      {slot.time}
                    </Button>
                  );
                })}
              </HStack>
            )}
          </Box>

        </VStack>
      )}

      {selectedSlot && selectedDate && (
        <Box ref={summaryRef} width={'100%'}>
          <Button onClick={handleContinue}>
            Continuar
          </Button>
        </Box>
      )}
    </VStack>
  );
}
